import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCustomerCart, saveCustomerCart, clearCustomerCart } from '../services/api';
import { toast } from 'sonner';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [customerToken, setCustomerToken] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const hasSyncedOnLogin = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('gelatti_cart');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart from localStorage:', e);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes (strip heavy base64 data if quota exceeded)
  useEffect(() => {
    try {
      localStorage.setItem('gelatti_cart', JSON.stringify(items));
    } catch (e) {
      try {
        const lightItems = items.map(item => ({
          ...item,
          image_url: item.image_url && item.image_url.startsWith('data:') ? null : item.image_url,
          file_url: item.file_url && item.file_url.startsWith('data:') ? null : item.file_url
        }));
        localStorage.setItem('gelatti_cart', JSON.stringify(lightItems));
      } catch (e2) {
        console.error('Error saving cart to localStorage:', e2);
      }
    }
  }, [items]);

  // Save to server with debounce (only when logged in)
  const saveToServer = useCallback(async (cartItems) => {
    if (!customerToken) return;
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce save
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        if (cartItems.length === 0) {
          await clearCustomerCart(customerToken);
        } else {
          await saveCustomerCart(customerToken, cartItems);
        }
      } catch (error) {
        console.error('Error saving cart to server:', error);
      }
    }, 500);
  }, [customerToken]);

  // Sync cart with server when customer logs in (ONLY ONCE)
  const syncCartWithServer = useCallback(async (token) => {
    if (!token || hasSyncedOnLogin.current) return;
    
    setIsSyncing(true);
    hasSyncedOnLogin.current = true;
    
    try {
      // Get cart from server
      const serverCart = await getCustomerCart(token);
      const serverItems = serverCart.items || [];
      
      // Get local cart
      const localItems = items;
      
      // If local cart is empty, use server cart
      if (localItems.length === 0 && serverItems.length > 0) {
        setItems(serverItems);
        toast.success('Seu carrinho foi recuperado!');
      }
      // If both have items, merge and prefer local (user's current session)
      else if (localItems.length > 0 && serverItems.length > 0) {
        const mergedItems = [...localItems];
        let addedCount = 0;
        
        serverItems.forEach(serverItem => {
          const exists = mergedItems.some(
            item => item.product_id === serverItem.product_id && 
                    item.customization_notes === serverItem.customization_notes
          );
          
          if (!exists) {
            mergedItems.push(serverItem);
            addedCount++;
          }
        });
        
        if (addedCount > 0) {
          setItems(mergedItems);
          await saveCustomerCart(token, mergedItems);
          toast.success(`Carrinho recuperado! ${addedCount} item(s) adicionado(s).`);
        } else {
          // Just save local to server
          await saveCustomerCart(token, localItems);
        }
      }
      // If only local has items, save to server
      else if (localItems.length > 0) {
        await saveCustomerCart(token, localItems);
      }
    } catch (error) {
      console.error('Error syncing cart:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [items]);

  // Called when customer logs in
  const onCustomerLogin = useCallback((token) => {
    setCustomerToken(token);
    hasSyncedOnLogin.current = false; // Reset sync flag for new login
    syncCartWithServer(token);
  }, [syncCartWithServer]);

  // Called when customer logs out
  const onCustomerLogout = useCallback(() => {
    setCustomerToken(null);
    hasSyncedOnLogin.current = false;
    // Clear local cart on logout - user's cart is saved on server
    setItems([]);
    localStorage.removeItem('gelatti_cart');
  }, []);

  // Save to server when items change (if logged in, after initial sync)
  useEffect(() => {
    if (customerToken && hasSyncedOnLogin.current && !isSyncing) {
      saveToServer(items);
    }
  }, [items, customerToken, isSyncing, saveToServer]);

  const addItem = (product, quantity = 1, customizationNotes = '') => {
    setItems(prev => {
      const existingIndex = prev.findIndex(
        item => item.product_id === product.id && item.customization_notes === customizationNotes
      );

      if (existingIndex > -1) {
        // Update quantity if same product with same notes
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }

      // Calculate unit price based on quantity
      let unitPrice = null;
      if (product.prices && product.prices.length > 0) {
        const priceRange = product.prices.find(p => {
          if (p.max_quantity === null) {
            return quantity >= p.min_quantity;
          }
          return quantity >= p.min_quantity && quantity <= p.max_quantity;
        });
        if (priceRange) {
          unitPrice = priceRange.price;
        }
      }

      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        quantity,
        customization_notes: customizationNotes,
        unit_price: unitPrice,
        image_url: product.image_url,
        file_url: product.file_url || null,
        art_link: product.art_link || null,
        art_type: product.art_type || null,
        art_description: product.art_description || null,
        image_adjustment: product.image_adjustment || null,
        is_pre_edited: product.is_pre_edited || false
      }];
    });
    setIsOpen(true);
  };

  const updateQuantity = (index, quantity) => {
    if (quantity < 1) {
      removeItem(index);
      return;
    }
    setItems(prev => {
      const updated = [...prev];
      updated[index].quantity = quantity;
      return updated;
    });
  };

  const updateNotes = (index, notes) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index].customization_notes = notes;
      return updated;
    });
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const clearCart = async () => {
    setItems([]);
    if (customerToken) {
      try {
        await clearCustomerCart(customerToken);
      } catch (error) {
        console.error('Error clearing cart on server:', error);
      }
    }
  };

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  
  const getTotalItems = () => items.reduce((sum, item) => sum + item.quantity, 0);
  const getTotalPrice = () => items.reduce((sum, item) => sum + (item.unit_price || 0) * item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      isOpen,
      addItem,
      updateQuantity,
      updateNotes,
      removeItem,
      clearCart,
      openCart,
      closeCart,
      getTotalItems,
      getTotalPrice,
      onCustomerLogin,
      onCustomerLogout,
      isCustomerLoggedIn: !!customerToken,
      isSyncing
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
